import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@libs/zapi-ui/shadcn";
import { getRouteDashboard } from "@/zapi-admin/shared/const/router";
import { UserMenu } from "@/zapi-admin/widgets/UserMenu";
import { useAdminStore } from "@/zapi-admin/stores/admin-store";
import { isNavGroup, isPathActive, navEntries, TNavGroup, TNavLink } from "../model/navConfig";

const baseLink = "px-3 py-2 rounded-md text-[14px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900";
const activeLink = "bg-gray-200 text-gray-900";
const dropdownItem = "text-[14px] text-gray-600 hover:bg-gray-100 hover:text-gray-900 cursor-pointer";
const activeDropdownItem = "bg-gray-100 text-gray-900";

export function AppNavbar() {
  const { user } = useAdminStore();
  const { pathname } = useLocation();
  const isSystemAdmin = user?.role === "systemAdmin";

  const visibleItems = (group: TNavGroup) => group.items.filter((item) => !item.systemAdminOnly || isSystemAdmin);

  const renderLink = (link: TNavLink) => (
    <NavLink
      key={link.to}
      to={link.to}
      className={({ isActive }) => `${baseLink} ${isActive ? activeLink : ""}`}
      data-testid={link.testId}
    >
      {link.label}
    </NavLink>
  );

  const renderGroup = (group: TNavGroup) => {
    const items = visibleItems(group);

    if (!items.length) {
      return null;
    }

    const isActive = items.some((item) => isPathActive(pathname, item));

    return (
      <DropdownMenu key={group.testId}>
        <DropdownMenuTrigger
          className={`${baseLink} flex items-center gap-1 ${isActive ? activeLink : ""}`}
          data-testid={group.testId}
        >
          {group.label}
          <ChevronDown className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {items.map((item) => (
            <DropdownMenuItem
              key={item.to}
              asChild
              className={`${dropdownItem} ${isPathActive(pathname, item) ? activeDropdownItem : ""}`}
            >
              <Link to={item.to} data-testid={item.testId}>
                {item.label}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <nav data-testid="admin-navbar">
      <div className="mx-auto container px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to={getRouteDashboard()} className="text-xl font-semibold text-gray-900" data-testid="nav-logo">
            Zapi-Admin
          </Link>

          <div className="flex items-center space-x-2">
            {navEntries.map((entry) => (isNavGroup(entry) ? renderGroup(entry) : renderLink(entry)))}
          </div>

          <UserMenu />
        </div>
      </div>
    </nav>
  );
}
